import { SearchFilteredResultsParams, Total } from "@/shared/search/search.types";
import { useDocumentSearch } from "../composables/document-search/document-search";
import { useDocumentGroupedSearch } from "@/abilities/documentation/composables/document-search/document-grouped-search.ts";
import { useDocumentationsAggregations } from "@/abilities/documentation/composables/document-search/documentations-aggregations.ts";
import { computed } from "vue";

/**
 * A composable function to handle document search and converts the search result to content results.
 *
 * Depending on the grouping state only one of both searches is executed: the grouped `contentsGrouped`
 * query while grouping is active, the plain `contents` query otherwise. `total` follows the same rule
 * and reports either the content total or the group total.
 *
 * @param selected - A reference to the selected facets for filtering.
 * @param facet - A reference to the current opened facet.
 * @param searchContext - A reference to the search context.
 * @param searchPhrase - A reference to the search phrase.
 * @param sort - A reference to the sort entry for sorting the search results.
 * @param groupingCriteria - A reference to the grouping criteria.
 * @returns An object containing the search configuration result, loading state, error state, total result, content results,
 *          functions to fetch more content results resp. more items of a single group and a function to fetch aggregations
 */
export const useFilteredDocumentationResults = ({
    selected,
    facet,
    searchContext,
    searchPhrase,
    sort,
    groupingCriteria,
}: SearchFilteredResultsParams) => {
    const isGroupingEnabled = computed(() => !!groupingCriteria?.value && !searchPhrase?.value?.length);
    const isPlainSearchEnabled = computed(() => !isGroupingEnabled.value);

    //search documents by given search params - only while the plain (ungrouped) result is displayed
    const {
        contents,
        total: contentsTotal,
        searchFields,
        loading: contentsLoading,
        error: contentsError,
        loadMoreContents,
    } = useDocumentSearch({
        searchContext,
        selected,
        sort,
        searchPhrase,
        enabled: isPlainSearchEnabled,
    });

    //fetch and map the grouped content results
    const {
        groupedContents,
        total: groupsTotal,
        loading: groupedLoading,
        error: groupedError,
        loadMoreGroups,
        loadMoreGroupItems,
    } = useDocumentGroupedSearch({
        groupingCriteria,
        isGroupingEnabled,
        selected,
        sort,
        searchContext,
    });

    const { fetchAggregation } = useDocumentationsAggregations({
        selectedFacets: selected,
        facet,
        searchFields,
        searchContext,
        searchPhrase,
    });

    const total = computed<Total | undefined>(() =>
        isGroupingEnabled.value ? groupsTotal.value : contentsTotal.value
    );

    const loading = computed(() => contentsLoading.value || (isGroupingEnabled.value && groupedLoading.value));

    //only the query that is actually running can report an error - the disabled one keeps its last state
    const error = computed(() => (isGroupingEnabled.value ? groupedError.value : contentsError.value));

    const loadMore = async (after: string, first: number) => {
        return isGroupingEnabled.value ? loadMoreGroups(after, first) : loadMoreContents(after, first);
    };

    return {
        loading,
        contents,
        groupedContents,
        total,
        error,
        loadMore,
        loadMoreGroupItems,
        fetchAggregation,
        isGroupingEnabled,
    };
};
